<template>
  <nav
    class="flex h-full"
    :class="{ 'absolute top-0 right-0 z-40 w-full': isMobile }"
    data-cy="nav-bar"
  >
    <div
      class="flex h-full flex-col space-y-8 py-4 pr-1.5"
      :class="isMobile ? 'w-9/12 bg-white pl-4' : 'w-32 items-center bg-light-grey'"
      @click="emit('close')"
    >
      <platform-branding-logo />
      <div
        class="z-30 flex h-full w-full flex-col justify-between space-y-16 overflow-y-auto"
        :class="{ 'items-start': isMobile }"
      >
        <div data-cy="top-menu-list" class="space-y-1" :class="{ 'w-[95%]': isMobile }">
          <platform-nav-bar-item
            v-for="item in topNavBarItems"
            :key="item.title"
            :icon="item.icon ?? DEFUALT_ICON"
            :title="item.title"
            :active="item.id === selectedTab?.id"
            :badge-count="item.type === TabType.documents ? documentsNeedingActionCount : 0"
            :data-cy="item.id"
            class="hover:bg-primary/10"
            @click="handleTabClick(item)"
          />
        </div>
        <div data-cy="bottom-menu-list" class="space-y-1" :class="{ 'w-[95%]': isMobile }">
          <platform-nav-bar-item
            v-for="item in bottomNavBarItems"
            :key="item.title"
            :icon="item.icon ?? DEFUALT_ICON"
            :title="item.title"
            :active="item.id === selectedTab?.id"
            :badge-count="item.type === TabType.documents ? documentsNeedingActionCount : 0"
            :data-cy="item.id"
            class="hover:bg-primary/10"
            @click="handleTabClick(item)"
          />
        </div>
      </div>
    </div>
    <div v-if="isMobile" class="h-full w-3/12 bg-gray-600/50" @click="emit('close')" />
    <platform-confirm-modal
      v-if="showLogOutModal"
      :title="logoutTab?.title ?? $t('auth.logout')"
      :confirm-button-variant="'secondary'"
      :confirm-button-text="$t('auth.logout')"
      @confirm="handleLogout"
      @close="showLogOutModal = false"
    >
      <div class="text-heading">{{ $t("auth.logOutConfirm") }}</div>
    </platform-confirm-modal>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useResponsive } from "@/composables/common";
import { useAnalyticsState } from "@/composables/state/analyticsState";
import useAuthState from "@/composables/state/authState";
import { useDocumentsState } from "@/composables/state/documentsState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import PlatformBrandingLogo from "@/components/platform/PlatformBrandingLogo.vue";
import PlatformConfirmModal from "@/components/platform/PlatformConfirmModal.vue";
import PlatformNavBarItem from "@/components/platform/PlatformNavBarItem.vue";
import { TabIcon, TabType } from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";
import { AnalyticsEvent } from "@/enums/analytics";

import type { Tab } from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";

const DEFUALT_ICON = TabIcon.Document;

const emit = defineEmits<{
  close: [];
}>();

const { logout } = useAuthState();
const { trackEvent } = useAnalyticsState();
const { selectedTab, visibleTabs, setSelectedTabById } = usePlatformScreenState();
const { documentsNeedingActionCount } = useDocumentsState();
const { isMobile } = useResponsive();

const showLogOutModal = ref(false);

const topNavBarItems = computed(() =>
  visibleTabs.value.filter((tab) => tab.navigation_position === "top")
);

const bottomNavBarItems = computed(() =>
  visibleTabs.value.filter((tab) => tab.navigation_position === "bottom")
);

const logoutTab = computed(() => visibleTabs.value.find((tab) => tab.type === TabType.logOut));

const handleTabClick = (tab: Tab) => {
  trackEvent(AnalyticsEvent.TabClicked, {
    tab_id: tab.id,
    tab_type: tab.type,
    tab_title: tab.title,
    navigation_position: tab.navigation_position,
    is_mobile: isMobile.value,
  });

  if (tab.type === TabType.logOut) {
    showLogOutModal.value = true;
    return;
  }

  setSelectedTabById(tab.id);
  emit("close");
};

const handleLogout = () => {
  logout();
  showLogOutModal.value = false;
};
</script>
